import React, { useState } from 'react';
import Layout from '../components/Layout';
import { useCompanies } from '../hooks/useCompanies';
import {
  Building2, Plus, ToggleLeft, ToggleRight,
  CheckCircle, AlertCircle, X,
} from 'lucide-react';

function Toast({ msg, ok }: { msg: string; ok: boolean }) {
  return (
    <div className={`fixed bottom-6 right-6 z-[999] flex items-center gap-3 px-5 py-3 rounded-2xl shadow-modal text-white text-sm font-medium animate-slide-up`}
      style={{ background: ok ? 'linear-gradient(135deg,#059669,#047857)' : 'linear-gradient(135deg,#dc2626,#b91c1c)' }}>
      {ok ? <CheckCircle className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
      {msg}
    </div>
  );
}

function Skeleton({ className }: { className?: string }) {
  return <div className={`skeleton rounded-xl ${className ?? ''}`} />;
}

const Companies: React.FC = () => {
  const { companies, loading, error, createCompany, toggleStatus } = useCompanies();
  const [showAdd, setShowAdd] = useState(false);
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);

  const showToast = (msg: string, ok = true) => { setToast({ msg, ok }); setTimeout(() => setToast(null), 3000); };

  const handleAdd = async () => {
    if (!name.trim()) { showToast('Company name is required', false); return; }
    setSaving(true);
    const { error: err } = await createCompany(name.trim());
    if (err) { showToast(err, false); } else {
      showToast('Company created successfully');
      setName(''); setShowAdd(false);
    }
    setSaving(false);
  };

  const handleToggle = async (id: string, status: 'active' | 'suspended') => {
    const { error: err } = await toggleStatus(id, status);
    if (err) { showToast(err, false); } else {
      showToast(`Company ${status === 'active' ? 'suspended' : 'activated'} successfully`);
    }
  };

  return (
    <Layout
      title="Companies"
      breadcrumbs={[{ label: 'Home', path: '/dashboard' }, { label: 'Companies' }]}
    >
      {/* Header */}
      <div className="mb-5 flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-slate-800" style={{ fontFamily: 'Space Grotesk, Inter, sans-serif' }}>
            All Taxi Companies
          </h2>
          <p className="text-sm text-slate-400 mt-0.5">Platform-level view — government admin only</p>
        </div>
        <button onClick={() => setShowAdd(true)} className="btn-primary gap-2">
          <Plus className="w-4 h-4" /> Add Company
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        {[
          { label: 'Total Companies', value: companies.length, color: '#06B6D4' },
          { label: 'Active',          value: companies.filter(c => c.status === 'active').length, color: '#10b981' },
          { label: 'Suspended',       value: companies.filter(c => c.status === 'suspended').length, color: '#ef4444' },
        ].map((s, i) => (
          <div key={i} className="card p-5 flex items-center gap-4">
            <Building2 className="w-8 h-8" style={{ color: s.color }} />
            <div>
              {loading ? <Skeleton className="h-7 w-10 mb-1" /> : (
                <div className="text-2xl font-bold text-slate-800" style={{ fontFamily: 'Space Grotesk' }}>{s.value}</div>
              )}
              <div className="text-xs text-slate-500">{s.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Table */}
      <div className="card overflow-hidden">
        {error && (
          <div className="px-5 py-3 bg-red-50 border-b border-red-100 text-sm text-red-600">{error}</div>
        )}
        <div className="overflow-x-auto">
          <table className="min-w-full table-premium">
            <thead>
              <tr>
                <th>Company Name</th>
                <th>Status</th>
                <th>Created</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                [...Array(4)].map((_, i) => (
                  <tr key={i}>
                    {[...Array(4)].map((__, j) => (
                      <td key={j}><Skeleton className="h-4 w-full" /></td>
                    ))}
                  </tr>
                ))
              ) : companies.length === 0 ? (
                <tr>
                  <td colSpan={4} className="text-center py-12 text-slate-400">
                    No companies yet. Add the first one.
                  </td>
                </tr>
              ) : companies.map((c) => (
                <tr key={c.id}>
                  <td>
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-xl bg-electric-50 flex items-center justify-center shrink-0">
                        <Building2 className="w-4 h-4 text-electric-500" />
                      </div>
                      <div>
                        <div className="font-semibold text-slate-800 text-sm">{c.name}</div>
                        <div className="text-xs text-slate-400 font-mono">{c.id.slice(0, 8)}…</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${
                      c.status === 'active'
                        ? 'bg-emerald-50 text-emerald-600 border border-emerald-100'
                        : 'bg-red-50 text-red-600 border border-red-100'
                    }`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${c.status === 'active' ? 'bg-emerald-500' : 'bg-red-500'}`} />
                      {c.status}
                    </span>
                  </td>
                  <td className="text-sm text-slate-500">
                    {new Date(c.created_at).toLocaleDateString()}
                  </td>
                  <td>
                    <div className="flex justify-end">
                      <button
                        onClick={() => handleToggle(c.id, c.status)}
                        title={c.status === 'active' ? 'Suspend' : 'Activate'}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition ${
                          c.status === 'active'
                            ? 'text-red-600 hover:bg-red-50 border border-red-100'
                            : 'text-emerald-600 hover:bg-emerald-50 border border-emerald-100'
                        }`}
                      >
                        {c.status === 'active'
                          ? <><ToggleLeft className="w-4 h-4" /> Suspend</>
                          : <><ToggleRight className="w-4 h-4" /> Activate</>
                        }
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add modal */}
      {showAdd && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-modal w-full max-w-md animate-slide-up overflow-hidden">
            <div className="px-6 py-4 flex items-center justify-between" style={{ background: 'linear-gradient(135deg, #0B1120, #0f2044)' }}>
              <h2 className="font-bold text-white" style={{ fontFamily: 'Space Grotesk, Inter, sans-serif' }}>New Company</h2>
              <button onClick={() => setShowAdd(false)} className="p-2 rounded-lg text-white/60 hover:text-white hover:bg-white/10 transition">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-600 mb-1.5">Company Name *</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
                  className="input-premium"
                  placeholder="e.g. Stockholm Taxi AB"
                  autoFocus
                />
              </div>
              <div className="flex gap-2 pt-1">
                <button onClick={handleAdd} disabled={saving} className="btn-primary text-sm px-6 py-2.5 disabled:opacity-60">
                  {saving ? 'Creating...' : 'Create Company'}
                </button>
                <button onClick={() => setShowAdd(false)} className="btn-ghost text-sm">Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {toast && <Toast msg={toast.msg} ok={toast.ok} />}
    </Layout>
  );
};

export default Companies;
